import { WhatsAppPage } from './whatsapp/WhatsAppPage';
import { AssistantPage } from './assistant/AssistantPage';
import { Loading } from '@ly360/ui';
import type { ReactNode } from 'react';
import { RouteError } from './components/RouteError';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { Shell } from './components/Shell';
import { useAuth } from './lib/auth';
import { AuditPage } from './pages/Audit';
import { CasePage, CasesPage } from './pages/Cases';
import { CompaniesPage, CompanyPage } from './pages/Companies';
import { DealsPage } from './pages/Deals';
import { HomePage } from './pages/Home';
import { IntegrationsPage } from './pages/Integrations';
import { PeoplePage } from './pages/People';
import { PersonPage } from './pages/Person';
import { TasksPage } from './pages/Tasks';
import { DeadLettersPage } from './pages/DeadLetters';
import { InboxPage } from './pages/Inbox';
import { Login } from './pages/Login';
import { SettingsPage } from './pages/Settings';
import { CalendarPage } from './pages/Calendar';
import { DuplicatesPage } from './pages/Duplicates';
import { TeamsPage } from './pages/Teams';
import { UsersPage } from './pages/Users';
import { ApprovalsPage, AutomationsPage } from './automations/Automations';
import { PublishingPage } from './publishing/Publishing';
import { CampaignPage, CampaignsPage } from './retail/Campaigns';
import { GrowthPage } from './retail/Growth';
import { OrdersPage } from './retail/Orders';
import { ProductsPage } from './retail/Products';
import { SegmentsPage } from './retail/Segments';

function RequireAuth({ children }: { children: ReactNode }) {
  const { session, ready } = useAuth();
  if (!ready) return <Loading />;
  if (!session) return <Navigate to="/login" replace />;
  return children;
}

const router = createBrowserRouter([
  { path: '/login', element: <Login />, errorElement: <RouteError /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      {
        errorElement: <RouteError />,
        children: [
          { index: true, element: <Navigate to="/home" replace /> },
          { path: 'home', element: <HomePage /> },
          { path: 'inbox', element: <InboxPage /> },
          { path: 'people', element: <PeoplePage /> },
          { path: 'people/duplicates', element: <DuplicatesPage /> },
          { path: 'people/:id', element: <PersonPage /> },
          { path: 'companies', element: <CompaniesPage /> },
          { path: 'companies/:id', element: <CompanyPage /> },
          { path: 'deals', element: <DealsPage /> },
          { path: 'cases', element: <CasesPage /> },
          { path: 'cases/:id', element: <CasePage /> },
          { path: 'tasks', element: <TasksPage /> },
          { path: 'calendar', element: <CalendarPage /> },
          { path: 'growth', element: <GrowthPage /> },
          { path: 'orders', element: <OrdersPage /> },
          { path: 'products', element: <ProductsPage /> },
          { path: 'campaigns', element: <CampaignsPage /> },
          { path: 'campaigns/:id', element: <CampaignPage /> },
          { path: 'segments', element: <SegmentsPage /> },
          { path: 'publishing', element: <PublishingPage /> },
          { path: 'automations', element: <AutomationsPage /> },
          { path: 'automations/approvals', element: <ApprovalsPage /> },
          { path: 'admin/integrations', element: <IntegrationsPage /> },
          { path: 'admin/assistant', element: <AssistantPage /> },
          { path: 'admin/whatsapp', element: <WhatsAppPage /> },
          { path: 'admin/users', element: <UsersPage /> },
          { path: 'admin/teams', element: <TeamsPage /> },
          { path: 'admin/audit', element: <AuditPage /> },
          { path: 'admin/failed-events', element: <DeadLettersPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: '*', element: <Navigate to="/home" replace /> },
        ],
      },
    ],
  },
]);

export function App() {
  const { ready } = useAuth();
  if (!ready) return <Loading />;
  return <RouterProvider router={router} />;
}
